// ============================================
// GuitarEngine - Motor del Mástil de Guitarra
// ============================================

export type SharpName = "C" | "C#" | "D" | "D#" | "E" | "F" | "F#" | "G" | "G#" | "A" | "A#" | "B";
export type FlatName  = "C" | "Db" | "D" | "Eb" | "E" | "F" | "Gb" | "G" | "Ab" | "A" | "Bb" | "B";
export type NoteName = SharpName | FlatName;

export type Degree = "R" | "b2" | "2" | "b3" | "3" | "4" | "#4" | "b5" | "5" | "#5" | "b6" | "6" | "bb7" | "b7" | "7" | "8";

export interface FretNote {
  string: number;
  fret: number;
  note: NoteName;
  octave: number;
  midi: number;
}

export interface NeckPosition {
  string: number;
  fret: number;
  note: NoteName;
  degree?: Degree;
  interval?: number;
  highlight?: "root" | "third" | "fifth" | "scale" | "active" | "muted";
  finger?: number;
  hand?: "left" | "right";
}

// Afinación estándar: E2 A2 D3 G3 B3 E4
export const OPEN_STRINGS: { note: NoteName; octave: number; midi: number }[] = [
  { note: "E", octave: 2, midi: 40 }, // Cuerda 6 (grave)
  { note: "A", octave: 2, midi: 45 }, // Cuerda 5
  { note: "D", octave: 3, midi: 50 }, // Cuerda 4
  { note: "G", octave: 3, midi: 55 }, // Cuerda 3
  { note: "B", octave: 3, midi: 59 }, // Cuerda 2
  { note: "E", octave: 4, midi: 64 }, // Cuerda 1 (aguda)
];

const SHARP_SCALE: SharpName[] = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const FLAT_SCALE:  FlatName[]  = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

export function noteToSemitones(note: NoteName): number {
  const map: Record<string, number> = {
    C: 0, "C#": 1, Db: 1, D: 2, "D#": 3, Eb: 3,
    E: 4, F: 5, "F#": 6, Gb: 6, G: 7, "G#": 8,
    Ab: 8, A: 9, "A#": 10, Bb: 10, B: 11,
  };
  return map[note] ?? 0;
}

/**
 * Convierte semitonos a nombre de nota usando enharmónicos correctos.
 * Para escalas con contexto: mayor usa sostenidos, menor usa bemoles.
 */
export function semitonesToNote(semitones: number, useFlats: boolean = false): NoteName {
  const idx = ((semitones % 12) + 12) % 12;
  return useFlats ? FLAT_SCALE[idx] : SHARP_SCALE[idx];
}

/**
 * Determina si una raíz de escala usa sistema de bemoles.
 * Basado en el círculo de quintas:
 * - Mayor con sostenidos: G, D, A, E, B, F#, C#
 * - Mayor con bemoles: F, Bb, Eb, Ab, Db, Gb, Cb
 * - C mayor: naturales
 * - Menor: deriva de su mayor relativa.
 */
export function shouldUseFlats(root: NoteName, scaleType: string): boolean {
  const rootSemi = noteToSemitones(root);

  if (scaleType === "major" || scaleType === "pentatonic_major") {
    // Semitonos de raíces mayores con bemoles: F(5), Bb(10), Eb(3), Ab(8)
    const flatMajors = new Set([5, 10, 3, 8]);
    return flatMajors.has(rootSemi);
  }

  if (scaleType.includes("minor") || scaleType === "blues" || scaleType === "pentatonic_minor") {
    // Mayor relativa = raíz menor + 3 semitonos
    const relativeMajorSemi = (rootSemi + 3) % 12;
    const flatMajors = new Set([5, 10, 3, 8]);
    return flatMajors.has(relativeMajorSemi);
  }

  return false;
}

/**
 * Calcula posiciones de trastes con espaciado logarítmico realista.
 * El traste 1 es más ancho que el 15.
 * Posición desde la cejuela: L * (1 - 2^(-n/12))
 */
export function getFretPositions(frets: number = 15, scaleLength: number = 1800): number[] {
  const positions: number[] = [0]; // cejuela en x = 0
  for (let i = 1; i <= frets; i++) {
    positions.push(scaleLength * (1 - Math.pow(2, -i / 12)));
  }
  return positions;
}

export function getNoteAtPosition(stringNum: number, fret: number): FretNote {
  const openString = OPEN_STRINGS[6 - stringNum];
  const midi = openString.midi + fret;
  const note = semitonesToNote(midi, false);
  const octave = openString.octave + Math.floor((openString.midi % 12 + fret) / 12);
  return { string: stringNum, fret, note, octave, midi };
}

export function generateNeckMap(frets: number = 15): FretNote[][] {
  const neck: FretNote[][] = [];
  for (let s = 6; s >= 1; s--) {
    const stringNotes: FretNote[] = [];
    for (let f = 0; f <= frets; f++) {
      stringNotes.push(getNoteAtPosition(s, f));
    }
    neck.push(stringNotes);
  }
  return neck;
}

export function findNotePositions(targetNote: NoteName, frets: number = 15, stringCount: number = 6): NeckPosition[] {
  const positions: NeckPosition[] = [];
  for (let s = 1; s <= stringCount; s++) {
    for (let f = 0; f <= frets; f++) {
      const pos = getNoteAtPosition(s, f);
      // Match por semitono, no por nombre string (para que C# = Db funcione)
      if (noteToSemitones(pos.note) === noteToSemitones(targetNote)) {
        positions.push({ string: s, fret: f, note: pos.note });
      }
    }
  }
  return positions;
}

// ============================================
// Motor de Acordes con SPAN controlado
// ============================================

export type InversionType = "fundamental" | "first" | "second";
export type ChordQuality = "major" | "minor" | "dim" | "aug";

export interface ChordVoicing {
  positions: NeckPosition[];
  mutedStrings: number[];
  inversion: InversionType;
  rootString: number;
}

const CHORD_INTERVALS: Record<ChordQuality, number[]> = {
  major: [0, 4, 7],
  minor: [0, 3, 7],
  dim: [0, 3, 6],
  aug: [0, 4, 8],
};

/**
 * Encuentra una posición de acorde ergonómica donde las voces suban de izquierda a derecha.
 * - Evita notas duplicadas (mismo semitono en dos cuerdas).
 * - Respeta span máximo de mano (5 trastes entre notas presionadas).
 * - Penaliza combinaciones abiertas + agudas extremas.
 */
export function buildChordVoicing(
  root: NoteName,
  quality: ChordQuality,
  inversion: InversionType,
  rootString: number,
  mode: "strum" | "arpeggio" = "strum"
): ChordVoicing {
  const intervals = CHORD_INTERVALS[quality];
  const rootSemitone = noteToSemitones(root);
  const useFlats = shouldUseFlats(root, quality === "minor" ? "minor" : "major");

  let intervalOrder: number[];
  let degreeOrder: Degree[];

  switch (inversion) {
    case "fundamental":
      intervalOrder = [intervals[0], intervals[1], intervals[2]];
      degreeOrder = ["R", quality === "minor" || quality === "dim" ? "b3" : "3", "5"];
      break;
    case "first":
      intervalOrder = [intervals[1], intervals[2], intervals[0]];
      degreeOrder = [quality === "minor" || quality === "dim" ? "b3" : "3", "5", "R"];
      break;
    case "second":
      intervalOrder = [intervals[2], intervals[0], intervals[1]];
      degreeOrder = ["5", "R", quality === "minor" || quality === "dim" ? "b3" : "3"];
      break;
  }

  // Recolectar todos los candidatos por voz (cuerda)
  const voiceCandidates: { string: number; fret: number; note: NoteName; interval: number; degree: Degree }[][] = [];

  for (let i = 0; i < 3; i++) {
    const targetString = rootString - i;
    if (targetString < 1) break;

    const targetInterval = intervalOrder[i];
    const targetNoteRaw = semitonesToNote(rootSemitone + targetInterval, useFlats);
    const cands: typeof voiceCandidates[0] = [];

    for (let f = 0; f <= 15; f++) {
      const pos = getNoteAtPosition(targetString, f);
      if (noteToSemitones(pos.note) === noteToSemitones(targetNoteRaw)) {
        cands.push({
          string: targetString,
          fret: f,
          note: targetNoteRaw,
          interval: targetInterval,
          degree: degreeOrder[i],
        });
      }
    }
    voiceCandidates.push(cands);
  }

  if (voiceCandidates.length === 0 || voiceCandidates.some(v => v.length === 0)) {
    return { positions: [], mutedStrings: [], inversion, rootString };
  }

  // Backtracking: buscar combo sin duplicados, voces ascendentes, span <= 5
  let bestCombo: typeof voiceCandidates[0] | null = null;
  let bestScore = Infinity;

  function backtrack(idx: number, current: typeof voiceCandidates[0], prevFret: number, usedSemitones: Set<number>) {
    if (idx === voiceCandidates.length) {
      const pressed = current.filter(c => c.fret > 0);
      const frets = pressed.map(c => c.fret);
      const span = frets.length > 1 ? Math.max(...frets) - Math.min(...frets) : 0;
      const maxFret = Math.max(...current.map(c => c.fret));
      const hasOpen = current.some(c => c.fret === 0);

      let score = span * 10 + maxFret * 2;
      if (span > 5) score += 1000;
      if (hasOpen && maxFret > 7) score += 500;
      if (maxFret > 12) score += 200;

      if (score < bestScore) {
        bestScore = score;
        bestCombo = [...current];
      }
      return;
    }

    for (const cand of voiceCandidates[idx]) {
      if (cand.fret < prevFret) continue;
      const semi = noteToSemitones(cand.note);
      if (usedSemitones.has(semi)) continue;

      usedSemitones.add(semi);
      current.push(cand);
      backtrack(idx + 1, current, cand.fret, usedSemitones);
      current.pop();
      usedSemitones.delete(semi);
    }
  }

  backtrack(0, [], -1, new Set());

  // Fallback: si no hay combo válido, permitir duplicados pero respetar ascenso y span
  if (!bestCombo || bestScore >= 1000) {
    bestScore = Infinity;
    bestCombo = null;
    function backtrackFallback(idx: number, current: typeof voiceCandidates[0], prevFret: number) {
      if (idx === voiceCandidates.length) {
        const pressed = current.filter(c => c.fret > 0);
        const frets = pressed.map(c => c.fret);
        const span = frets.length > 1 ? Math.max(...frets) - Math.min(...frets) : 0;
        const maxFret = Math.max(...current.map(c => c.fret));
        const hasOpen = current.some(c => c.fret === 0);

        let score = span * 10 + maxFret * 2;
        if (span > 5) score += 1000;
        if (hasOpen && maxFret > 7) score += 500;

        if (score < bestScore) {
          bestScore = score;
          bestCombo = [...current];
        }
        return;
      }
      for (const cand of voiceCandidates[idx]) {
        if (cand.fret < prevFret) continue;
        current.push(cand);
        backtrackFallback(idx + 1, current, cand.fret);
        current.pop();
      }
    }
    backtrackFallback(0, [], -1);
  }

  const bestPositions: NeckPosition[] = (bestCombo || []).map((c, i) => ({
    string: c.string,
    fret: c.fret,
    note: c.note,
    degree: c.degree,
    interval: c.interval,
    highlight: c.degree === "R" ? "root" : c.degree === "5" ? "fifth" : "third",
    finger: i + 1,
  }));

  const usedStrings = bestPositions.map(p => p.string);
  const mutedStrings: number[] = [];
  if (mode === "strum") {
    for (let s = 6; s >= 1; s--) {
      if (!usedStrings.includes(s)) mutedStrings.push(s);
    }
  }

  return { positions: bestPositions, mutedStrings, inversion, rootString };
}

// ============================================
// Escalas y Patrones
// ============================================

export const SCALE_INTERVALS: Record<string, number[]> = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  minor_harmonic: [0, 2, 3, 5, 7, 8, 11],
  minor_melodic: [0, 2, 3, 5, 7, 9, 11],
  pentatonic_major: [0, 2, 4, 7, 9],
  pentatonic_minor: [0, 3, 5, 7, 10],
  blues: [0, 3, 5, 6, 7, 10],
  chromatic: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
};

export interface ScalePattern {
  root: NoteName;
  scaleType: string;
  positions: NeckPosition[];
  notes: NoteName[];
  degrees: string[];
}

export function buildScalePattern(root: NoteName, scaleType: string, frets: number = 15, stringCount: number = 6): ScalePattern {
  const pattern = SCALE_INTERVALS[scaleType] ?? SCALE_INTERVALS.major;
  const useFlats = shouldUseFlats(root, scaleType);
  const rootSemitone = noteToSemitones(root);

  // Build scale notes with correct enharmonics
  const scaleNotes = pattern.map(interval => semitonesToNote(rootSemitone + interval, useFlats));
  const scaleNoteSet = new Set(scaleNotes.map(n => noteToSemitones(n)));

  const positions: NeckPosition[] = [];
  for (let s = 1; s <= stringCount; s++) {
    for (let f = 0; f <= frets; f++) {
      const pos = getNoteAtPosition(s, f);
      const semitone = noteToSemitones(pos.note);
      if (scaleNoteSet.has(semitone)) {
        const interval = ((pos.midi - rootSemitone) % 12 + 12) % 12;
        // Use the correct enharmonic name from scaleNotes
        const correctNote = scaleNotes.find(n => noteToSemitones(n) === semitone) || pos.note;
        
        // Asignar highlight y degree según el grado funcional
        let highlight: NeckPosition["highlight"] = "scale";
        if (interval === 0) highlight = "root";
        else if (interval === 3 || interval === 4) highlight = "third";
        else if (interval === 7) highlight = "fifth";

        const degreeByInterval: Record<number, string> = {
          0: "R", 1: "b2", 2: "2", 3: "b3", 4: "3",
          5: "4", 6: "#4", 7: "5", 8: "b5", 9: "6",
          10: "b7", 11: "7",
        };

        positions.push({
          string: s,
          fret: f,
          note: correctNote,
          interval,
          highlight,
          degree: degreeByInterval[interval] as any,
        });
      }
    }
  }

  // Build degree names (notación funcional consistente con fingeringEngine)
  const degreeMap: Record<string, string[]> = {
    major: ["R", "2", "3", "4", "5", "6", "7"],
    minor: ["R", "2", "b3", "4", "5", "b6", "b7"],
    minor_harmonic: ["R", "2", "b3", "4", "5", "b6", "7"],
    minor_melodic: ["R", "2", "b3", "4", "5", "6", "7"],
    pentatonic_major: ["R", "2", "3", "5", "6"],
    pentatonic_minor: ["R", "b3", "4", "5", "b7"],
    blues: ["R", "b3", "4", "#4/b5", "5", "b7"],
    chromatic: ["R", "b2", "2", "b3", "3", "4", "#4", "5", "b6", "6", "b7", "7"],
  };

  return {
    root,
    scaleType,
    positions,
    notes: scaleNotes,
    degrees: degreeMap[scaleType] || degreeMap.major,
  };
}

/**
 * Genera una secuencia de posiciones para estudio de escala.
 * Puede recorrer la escala nota por nota, por saltos, o en patrones.
 */
export function buildScaleSequence(
  root: NoteName,
  scaleType: string,
  options: {
    octaves?: number;
    direction?: "asc" | "desc" | "both";
    pattern?: "linear" | "thirds" | "fourths" | "fifths" | "custom";
    customOrder?: number[];
    startFret?: number;
    stringCount?: number;
  } = {}
): NeckPosition[] {
  const { octaves = 1, direction = "asc", pattern = "linear", customOrder, startFret = 0, stringCount = 6 } = options;

  const scalePattern = buildScalePattern(root, scaleType, 15, stringCount);
  const scaleNotes = scalePattern.notes;
  const numNotes = scaleNotes.length;

  let indices: number[] = [];

  if (pattern === "linear") {
    for (let oct = 0; oct < octaves; oct++) {
      for (let i = 0; i < numNotes; i++) indices.push(i);
    }
    if (direction === "desc" || direction === "both") {
      for (let i = numNotes - 2; i >= 0; i--) indices.push(i);
    }
  } else if (pattern === "thirds") {
    for (let i = 0; i < numNotes; i++) indices.push(i, (i + 2) % numNotes);
  } else if (pattern === "fourths") {
    for (let i = 0; i < numNotes; i++) indices.push(i, (i + 3) % numNotes);
  } else if (pattern === "fifths") {
    for (let i = 0; i < numNotes; i++) indices.push(i, (i + 4) % numNotes);
  } else if (pattern === "custom" && customOrder) {
    indices = customOrder;
  }

  const sequence: NeckPosition[] = [];
  let currentString = stringCount;
  let currentFret = startFret;
  let sameStringCount = 0;

  for (const idx of indices) {
    const targetNote = scaleNotes[idx % numNotes];
    const targetSemitone = noteToSemitones(targetNote);

    let bestPos: NeckPosition | null = null;
    let bestScore = Infinity;

    // Buscar en todas las cuerdas, no solo desde la actual hacia abajo
    for (let s = stringCount; s >= 1; s--) {
      for (let f = Math.max(0, currentFret - 3); f <= Math.min(15, currentFret + 5); f++) {
        const pos = getNoteAtPosition(s, f);
        if (noteToSemitones(pos.note) === targetSemitone) {
          let score = Math.abs(s - currentString) * 3 + Math.abs(f - currentFret);
          // Penalty por quedarse en la misma cuerda después de 2 notas consecutivas
          if (s === currentString && sameStringCount >= 2) {
            score += 25;
          }
          // Bonus por cambiar de cuerda (distribuye la escala por el mástil)
          if (s !== currentString) {
            score -= 3;
          }
          if (score < bestScore) {
            bestScore = score;
            bestPos = { string: s, fret: f, note: targetNote, degree: scalePattern.degrees[idx % numNotes] as Degree, highlight: idx % numNotes === 0 ? "root" : "scale" };
          }
        }
      }
    }

    if (!bestPos) {
      for (let s = stringCount; s >= 1; s--) {
        for (let f = 0; f <= 15; f++) {
          const pos = getNoteAtPosition(s, f);
          if (noteToSemitones(pos.note) === targetSemitone) {
            let score = Math.abs(s - currentString) * 3 + Math.abs(f - currentFret);
            if (s === currentString && sameStringCount >= 2) {
              score += 25;
            }
            if (s !== currentString) {
              score -= 3;
            }
            if (score < bestScore) {
              bestScore = score;
              bestPos = { string: s, fret: f, note: targetNote, degree: scalePattern.degrees[idx % numNotes] as Degree, highlight: idx % numNotes === 0 ? "root" : "scale" };
            }
          }
        }
      }
    }

    if (bestPos) {
      if (bestPos.string === currentString) {
        sameStringCount++;
      } else {
        sameStringCount = 0;
      }
      bestPos.finger = ((sequence.length % 4) + 1);
      sequence.push(bestPos);
      currentString = bestPos.string;
      currentFret = bestPos.fret;
    }
  }

  return sequence;
}

/**
 * Genera una secuencia de escala respetando la capacidad de la mano humana.
 * Cada nota siguiente debe estar dentro de maxFretSpan trastes de distancia,
 * fomentando la conexión entre cuerdas y posiciones cercanas.
 */
export function buildScaleHandFriendlySequence(
  root: NoteName,
  scaleType: string,
  options: {
    octaves?: number;
    direction?: "asc" | "desc" | "both";
    maxFretSpan?: number;
    startString?: number;
    startFret?: number;
    stringCount?: number;
  } = {}
): NeckPosition[] {
  const { octaves = 1, direction = "asc", maxFretSpan = 5, startString = 6, startFret = 0, stringCount = 6 } = options;

  const scalePattern = buildScalePattern(root, scaleType, 15, stringCount);
  const scaleNotes = scalePattern.notes;
  const numNotes = scaleNotes.length;

  let indices: number[] = [];
  for (let oct = 0; oct < octaves; oct++) {
    for (let i = 0; i < numNotes; i++) indices.push(i);
  }
  if (direction === "desc" || direction === "both") {
    for (let i = numNotes - 2; i >= 0; i--) indices.push(i);
  }

  const sequence: NeckPosition[] = [];
  let currentString = Math.min(startString, stringCount);
  let currentFret = startFret;
  let sameStringCount = 0;

  for (const idx of indices) {
    const targetNote = scaleNotes[idx % numNotes];
    const targetSemitone = noteToSemitones(targetNote);
    const degree = scalePattern.degrees[idx % numNotes] as Degree;

    let bestPos: NeckPosition | null = null;
    let bestScore = Infinity;

    for (let s = stringCount; s >= 1; s--) {
      for (let f = 0; f <= 15; f++) {
        const pos = getNoteAtPosition(s, f);
        if (noteToSemitones(pos.note) !== targetSemitone) continue;

        const fretDist = Math.abs(f - currentFret);
        const stringDist = Math.abs(s - currentString);

        if (fretDist <= maxFretSpan) {
          let score = stringDist * 10 + fretDist;
          // Penalty por quedarse en la misma cuerda después de 2 notas consecutivas
          if (s === currentString && sameStringCount >= 2) {
            score += 40;
          }
          // Bonus por cambiar de cuerda
          if (s !== currentString) {
            score -= 4;
          }
          if (score < bestScore) {
            bestScore = score;
            bestPos = { string: s, fret: f, note: targetNote, degree, highlight: idx % numNotes === 0 ? "root" : "scale" };
          }
        }
      }
    }

    if (!bestPos) {
      let fallbackScore = Infinity;
      for (let s = stringCount; s >= 1; s--) {
        for (let f = 0; f <= 15; f++) {
          const pos = getNoteAtPosition(s, f);
          if (noteToSemitones(pos.note) !== targetSemitone) continue;
          let score = Math.abs(f - currentFret) + Math.abs(s - currentString) * 3;
          if (s === currentString && sameStringCount >= 2) {
            score += 40;
          }
          if (s !== currentString) {
            score -= 4;
          }
          if (score < fallbackScore) {
            fallbackScore = score;
            bestPos = { string: s, fret: f, note: targetNote, degree, highlight: idx % numNotes === 0 ? "root" : "scale" };
          }
        }
      }
    }

    if (bestPos) {
      if (bestPos.string === currentString) {
        sameStringCount++;
      } else {
        sameStringCount = 0;
      }
      bestPos.finger = ((sequence.length % 4) + 1);
      sequence.push(bestPos);
      currentString = bestPos.string;
      currentFret = bestPos.fret;
    }
  }

  return sequence;
}

// ============================================
// Pitch Detection Utils
// ============================================

export function frequencyToMidi(frequency: number): number {
  if (frequency <= 0) return 0;
  return 69 + 12 * Math.log2(frequency / 440);
}

export function midiToNoteName(midi: number, useFlats: boolean = false): { note: NoteName; octave: number } {
  const semitone = Math.round(midi) % 12;
  const octave = Math.floor(Math.round(midi) / 12) - 1;
  return { note: semitonesToNote(semitone, useFlats), octave };
}

export function frequencyToNoteName(frequency: number, useFlats: boolean = false): { note: NoteName; octave: number; cents: number } {
  const midi = frequencyToMidi(frequency);
  const roundedMidi = Math.round(midi);
  const cents = (midi - roundedMidi) * 100;
  const semitone = ((roundedMidi % 12) + 12) % 12;
  const octave = Math.floor(roundedMidi / 12) - 1;
  return { note: semitonesToNote(semitone, useFlats), octave, cents };
}

// ============================================
// MusicXML Parser
// ============================================

export interface MusicXmlNote {
  step: string;
  alter?: number;
  octave: number;
  duration: number;
  type: string;
  midi: number;
  hasDot?: boolean;
  isRest?: boolean;
}

export function parseMusicXml(xmlString: string): MusicXmlNote[] {
  const notes: MusicXmlNote[] = [];
  const noteRegex = /<note[\s>][\s\S]*?<\/note>/g;
  const notesXml = xmlString.match(noteRegex) || [];

  for (const noteXml of notesXml) {
    const stepMatch = noteXml.match(/<step>([A-G])<\/step>/);
    const octaveMatch = noteXml.match(/<octave>(\d)<\/octave>/);
    const alterMatch = noteXml.match(/<alter>(-?\d)<\/alter>/);
    const durationMatch = noteXml.match(/<duration>(\d)<\/duration>/);
    const typeMatch = noteXml.match(/<type>(\w+)<\/type>/);

    if (stepMatch && octaveMatch) {
      const step = stepMatch[1];
      const octave = parseInt(octaveMatch[1]);
      const alter = alterMatch ? parseInt(alterMatch[1]) : 0;
      const baseSemitone: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
      const semitone = baseSemitone[step] + alter;
      const midi = (octave + 1) * 12 + semitone;

      notes.push({
        step,
        alter: alter !== 0 ? alter : undefined,
        octave,
        duration: durationMatch ? parseInt(durationMatch[1]) : 1,
        type: typeMatch ? typeMatch[1] : "quarter",
        midi,
      });
    }
  }
  return notes;
}

// ============================================
// Configuración del SVG del Mástil
// ============================================

export const NECK_CONFIG = {
  strings: 6,
  frets: 15,
  stringSpacing: 48,
  scaleLength: 1800, // longitud virtual de escala para cálculo de trastes progresivos
  nutWidth: 10,
  dotFrets: [3, 5, 7, 9, 12, 15],
  doubleDotFrets: [12],
  width: 1400,
  height: 340,
  paddingLeft: 80,
  paddingTop: 40,
};

// ============================================
// Geometría compartida del mástil (SVG ↔ HTML)
// ============================================

export interface NeckGeometry {
  /** Porcentaje del ancho total donde cae cada línea de traste (0..frets) */
  fretPercents: number[];
  /** Porcentaje donde empieza el board (nut line) */
  boardLeftPercent: number;
  /** Porcentaje del ancho del board (último traste - nut) */
  boardWidthPercent: number;
  /** Ancho total del viewBox del SVG */
  totalWidth: number;
}

export interface NeckGeometryOptions {
  frets: number;
  scaleLength: number;
  targetBoardWidth: number;
  paddingLeft: number;
  paddingRight?: number;
}

/**
 * Calcula la geometría normalizada del mástil para alinear componentes
 * HTML (barras, tooltips) con el SVG de GuitarNeck.
 * Los porcentajes son relativos al viewBox total del SVG.
 */
export function getNeckGeometry(options: NeckGeometryOptions): NeckGeometry {
  const { frets, scaleLength, targetBoardWidth, paddingLeft, paddingRight = 80 } = options;
  const zoomPaddingLeft = paddingLeft * 1.3;

  const raw = getFretPositions(frets, scaleLength);
  const scale = targetBoardWidth / raw[raw.length - 1];
  const fretXs = raw.map((p) => p * scale);
  const lastFretX = fretXs[fretXs.length - 1];
  const totalWidth = zoomPaddingLeft + lastFretX + paddingRight;

  const fretPercents = fretXs.map((x) => ((zoomPaddingLeft + x) / totalWidth) * 100);
  // Traste 0 (cejuela) está en zoomPaddingLeft
  fretPercents[0] = (zoomPaddingLeft / totalWidth) * 100;

  return {
    fretPercents,
    boardLeftPercent: fretPercents[0],
    boardWidthPercent: (lastFretX / totalWidth) * 100,
    totalWidth,
  };
}
